import { z } from 'zod';
import { LOCALES } from '../locales.js';
import { slugSchema } from './taxonomy.js';
/**
 * Public read API request schemas — phases.md Phase 6, §2.
 *
 * Two pagination styles live here on purpose (§2): public feeds are **cursor**-paginated,
 * because a reader scrolling a feed while stories publish underneath them must never see
 * the same article twice or skip one, which offset pagination cannot promise. The admin
 * tables in `content.ts` stay offset-based, because an editor wants page 4 of their desk.
 */
export const localeSchema = z.enum(LOCALES);
/**
 * Cursor tokens are opaque to the client by contract: `base64url("<publishedAtMs>:<id>")`.
 * Validated only as "plausible token" here — decoding happens server-side, and a token
 * that survives this schema but not the decoder is a 400 either way.
 */
export const cursorSchema = z
    .string()
    .trim()
    .max(120)
    .regex(/^[A-Za-z0-9_-]+$/, 'Malformed cursor');
const feedLimit = z.coerce.number().int().positive().max(100).default(20);
export const publicFeedQuerySchema = z.object({
    /** Required: no public feed ever spans both locales (§1.7/§1.15). */
    locale: localeSchema,
    category: slugSchema.optional(),
    district: slugSchema.optional(),
    tag: slugSchema.optional(),
    author: slugSchema.optional(),
    cursor: cursorSchema.optional(),
    limit: feedLimit,
});
export const SEARCH_SORTS = ['relevance', 'newest', 'most_viewed'];
/**
 * A date the reader picked, turned into the instant the newsroom means by it.
 *
 * `2026-09-18` is a day in Assam, not a day in UTC: taken literally it would start at
 * 05:30 IST and end five and a half hours before the reader's evening. So a bare date is
 * read as the IST day it names — the start of it for `from`, the last millisecond of it
 * for `to`, so that a range of one day contains that day's stories (§1.12: stored UTC,
 * displayed `Asia/Kolkata`). A full timestamp is still accepted as given.
 */
const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;
export function istDay(edge) {
    return z
        .string()
        .transform((value) => BARE_DATE.test(value)
        ? new Date(`${value}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}+05:30`)
        : new Date(value))
        .refine((date) => !Number.isNaN(date.getTime()), { message: 'Invalid date' })
        .describe('`YYYY-MM-DD` (read as an Asia/Kolkata day) or an ISO-8601 timestamp');
}
export const publicSearchQuerySchema = z.object({
    locale: localeSchema,
    /** One character is a legitimate query in Assamese; §1.7's `LIKE` fallback handles it. */
    q: z.string().trim().min(1).max(200),
    category: slugSchema.optional(),
    district: slugSchema.optional(),
    author: slugSchema.optional(),
    from: istDay('start').optional(),
    to: istDay('end').optional(),
    sort: z.enum(SEARCH_SORTS).default('relevance'),
    /** Offset-paginated: relevance ranking has no stable keyset to page by. */
    page: z.coerce.number().int().positive().max(50).default(1),
    limit: feedLimit,
});
export const publicHomepageQuerySchema = z.object({
    locale: localeSchema,
});
export const publicLocaleQuerySchema = publicHomepageQuerySchema;
export const publicVideoQuerySchema = z.object({
    locale: localeSchema,
    category: slugSchema.optional(),
    cursor: cursorSchema.optional(),
    limit: feedLimit,
});
export const publicTrendingQuerySchema = z.object({
    locale: localeSchema,
    limit: z.coerce.number().int().positive().max(50).default(10),
});
export const publicLiveUpdatesQuerySchema = z.object({
    /** Newest-first pages of one event's timeline. */
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(50),
});
/**
 * §1.8 SSE resumption. `Last-Event-ID` is the standard header, but EventSource cannot set
 * headers on the initial connection, so the query parameter is the first-connect form of
 * the same thing.
 */
export const streamQuerySchema = z.object({
    locale: localeSchema.optional(),
    lastEventId: z.coerce.number().int().nonnegative().optional(),
});
//# sourceMappingURL=public.js.map